import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import WebSocketManager from 'services/websocketManager';
import { WS_STATUS } from 'constants/wsStatus';
import { useUserDetailsContext } from './userDetailsContext';
import { notificationTypes } from 'constants';
import { postActivityLog } from 'services/generalServices';

const WebSocketContext = createContext(null);

const logWsError = (payload, error) => {
  try {
    postActivityLog(
      {
        event_items: {
          payload: payload,
          errorMessage: error?.message,
          error: JSON.stringify(error),
        },
        event_type: 'ws_error',
      },
      true
    );
  } catch (error) {
    console.error('Error logging WS error:', error);
  }
};

export const WebSocketContextProvider = ({ children }) => {
  const wsRef = useRef(null);
  const [status, setStatus] = useState(WS_STATUS.DISCONNECTED);

  const {
    firmDetails: { tenant_id, current_user, access_level },
  } = useUserDetailsContext();

  useEffect(() => {
    let ws = null;
    if (tenant_id && current_user) {
      ws = new WebSocketManager({
        baseUrl: process.env.REACT_APP_WEB_SOCKET_URL,
        tenantId: tenant_id,
        userId: current_user,
        accessLevel: access_level,
        getToken: async () => {
          const session = await Auth.currentSession();
          return session.getIdToken().getJwtToken();
        },
        pingInterval: 60000, // Send heartbeat every 60 seconds
        pongTimeout: 5000, // Wait 5 seconds for response
        maxMissedHeartbeats: 2, // Fail after 2 missed heartbeats (prevents false disconnects)
        maxReconnectAttempts: 4,
        reconnectInterval: 3000,
        longRetryInterval: 10000, // Keep trying every 10 seconds after max attempts,
        onSendError: logWsError,
      });
      wsRef.current = ws;
      ws.on('STATUS_CHANGE', setStatus);
      ws.connect();
    }
    return () => {
      if (ws) {
        ws.disconnect();
      }
    };
  }, [tenant_id, current_user, access_level]);

  const send = async (payload) => {
    return wsRef.current?.send(payload);
  };

  const subscribe = (type, callback) => {
    return wsRef.current?.on(type, callback);
  };

  const unsubscribe = (type, callback) => {
    wsRef.current?.off(type, callback);
  };

  const disconnect = () => {
    wsRef.current?.disconnect();
  };

  const reconnect = () => {
    wsRef.current?.reconnect();
  };

  // Added to handle backward compatibility with the old websocket manager
  const [notificationPopup, setNotificationPopup] = useState(false);
  const [notificationData, setNotificationData] = useState();
  const [response, setResponse] = useState();
  const [secureMessageData, setSecureMessageData] = useState();
  const [sideBarMessages, setSideBarMessages] = useState();
  const [secureMessageError, setSecureMessageError] = useState();
  const [assignedTaskData, setAssignedTaskData] = useState();
  const [textMessageData, setTextMessageData] = useState();
  const [clientLogout, setClientLogout] = useState();
  const [automationErrorLogData, setAutomationErrorLogData] = useState();
  const [aiFileSummary, setAiFileSummary] = useState();
  const [caseEmails, setCaseEmails] = useState();
  const [aiCourtDate, setAiCourtDate] = useState();
  const [aiEntitiesData, setAiEntitiesData] = useState();
  const [aiTimelineData, setAiTimelineData] = useState();
  const [folderDownloadData, setFolderDownloadData] = useState();
  const [caseNotePDFDownloadData, setCaseNotePDFDownloadData] = useState();

  useEffect(() => {
    const handler = (data) => {
      if (data) {
        if (data?.notification_type === 'secure_message' && data?.show_in_notification_tray === false) {
          return;
        }
        if (notificationTypes?.includes(data?.notification_type)) {
          setNotificationData(data);
        }

        if (data?.notification_type === 'server_status') {
          if (data?.body?.execution_status === 'success') {
            setResponse({ ...data });
          } else {
            setSecureMessageError({ ...data });
          }
        } else if (data?.notification_type === 'sidebar_message') {
          setSideBarMessages({ ...data });
        } else if (data?.notification_type === 'secure_message') {
          setSecureMessageData({ ...data });
        } else if (data?.notification_type === 'assignment') {
          setAssignedTaskData({ ...data });
        } else if (data?.notification_type === 'firm_Admin_change') {
          setAssignedTaskData({ ...data });
        } else if (data?.notification_type === 'firm_deactivated') {
          setAssignedTaskData({ ...data });
        } else if (data?.notification_type === 'inbound_text_message' || data?.notification_type === 'text_message') {
          setTextMessageData({ ...data });
        } else if (data?.notification_type === 'outbound_status_callback') {
          setTextMessageData({ ...data });
        } else if (data?.notification_type === 'automation_error_logs') {
          setAutomationErrorLogData({ ...data });
        } else if (data && ['user_log_out', 'notification_type', 'archive_user', 'user_data_changed'].includes(data?.notification_type)) {
          setClientLogout({ ...data });
        } else if (data?.notification_type === 'ai_summary_message') {
          setAiFileSummary({ ...data });
        } else if (data?.notification_type === 'incoming_email' || data?.notification_type === 'email_is_received') {
          setCaseEmails({ ...data });
        } else if (data?.notification_type === 'ai_court_date_message') {
          setAiCourtDate({ ...data });
        } else if (data?.notification_type === 'ai_entity_message') {
          setAiEntitiesData({ ...data });
        } else if (data?.notification_type === 'ai_timeline_message') {
          setAiTimelineData({ ...data });
        } else if (data?.notification_type === 'get_folder_content_message') {
          setFolderDownloadData({ ...data });
        } else if (data?.notification_type === 'get_case_pdf_message') {
          setCaseNotePDFDownloadData({ ...data });
        }
      }
    };
    subscribe('ALL', handler);
    return () => {
      unsubscribe('ALL', handler);
    };
  }, []);

  const contextValue = {
    notificationPopup: notificationPopup,
    notificationData: notificationData,
    setNotificationPopup: setNotificationPopup,
    sendMessage: send,
    closeConnection: disconnect,
    response: response,
    setResponse: setResponse,
    setSecureMessageData: setSecureMessageData,
    secureMessageData: secureMessageData,
    secureMessageError: secureMessageError,
    setSecureMessageError: setSecureMessageError,
    setNotificationData: setNotificationData,
    assignedTask: assignedTaskData,
    setAssignedTaskData: setAssignedTaskData,
    textMessageData: textMessageData,
    setTextMessageData: setTextMessageData,
    clientLogout: clientLogout,
    automationErrorLogData: automationErrorLogData,
    setAutomationErrorLogData: setAutomationErrorLogData,
    sideBarMessages,
    setSideBarMessages,
    aiFileSummary: aiFileSummary,
    caseEmails: caseEmails,
    aiCourtDate: aiCourtDate,
    setAiCourtDate: setAiCourtDate,
    aiEntitiesData: aiEntitiesData,
    aiTimelineData: aiTimelineData,
    folderDownloadData: folderDownloadData,
    caseNotePDFDownloadData: caseNotePDFDownloadData,
    isConnected: status === WS_STATUS.CONNECTED,
  };

  // ------------------------------------------------------------

  return (
    <WebSocketContext.Provider value={{ send, subscribe, unsubscribe, reconnect, status, ...contextValue }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used inside WebSocketProvider');
  }
  return context;
};

export const useWebSocketContext = () => useContext(WebSocketContext);

export const WebSocketConnectionContext = WebSocketContext;
